import { useCallback, useEffect, useMemo, useState } from "react";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import OutlinedInput from "@mui/material/OutlinedInput";
import Checkbox from "@mui/material/Checkbox";
import ListItemText from "@mui/material/ListItemText";
import VuiBox from "components/VuiBox";
import VuiButton from "components/VuiButton";
import VuiInput from "components/VuiInput";
import VuiTypography from "components/VuiTypography";
import LoadingSpinner from "components/LoadingSpinner";
import { useAuthz } from "context/AuthzContext";
import { useCompany } from "context/CompanyContext";
import {
  assignTenantUserRoles,
  createTenantRole,
  listTenantRoles,
  listTenantUsers,
} from "services/tenantUserAdminService";
import { PERMISSION_LABELS } from "auth/permissions";
import { TC } from "../tokens/text";

const SELECT_MENU_PROPS = {
  PaperProps: {
    sx: {
      maxHeight: 320,
      mt: 1,
      bgcolor: 'background.paper',
      color: 'text.primary',
      border: ({ palette: { grey } }) => `1px solid ${grey[500]}`,
      boxShadow: 4,
      "& .MuiMenuItem-root": {
        color: 'text.primary',
        minHeight: 48,
      },
      "& .MuiMenuItem-root.Mui-selected": {
        backgroundColor: "rgba(0, 117, 255, 0.14)",
      },
      "& .MuiMenuItem-root.Mui-selected:hover": {
        backgroundColor: "rgba(0, 117, 255, 0.22)",
      },
      "& .MuiMenuItem-root:hover": {
        backgroundColor: ({ palette }) => palette.action.hover,
      },
      "& .MuiCheckbox-root": {
        color: TC.midtone,
      },
      "& .MuiCheckbox-root.Mui-checked": {
        color: "info.main",
      },
      "& .MuiListItemText-primary": {
        color: 'text.primary',
        fontSize: '14px',
      },
    },
  },
  MenuListProps: {
    dense: true,
  },
};

const selectFieldSx = {
  "& .MuiInputBase-root": {
    backgroundColor: ({ palette: { inputColors } }) => `${inputColors.backgroundColor} !important`,
    color: ({ palette: { text } }) => `${text.primary} !important`,
    borderRadius: ({ borders: { borderRadius } }) => borderRadius.lg,
    border: ({ palette: { grey } }) => `0.5px solid ${grey[600]}`,
    minHeight: 44,
  },
  "& .MuiOutlinedInput-notchedOutline": {
    border: "none",
  },
  "& .MuiSvgIcon-root": {
    color: ({ palette: { text } }) => `${text.primary} !important`,
  },
  "& .MuiInputLabel-root": {
    color: ({ palette: { text } }) => `${text.primary} !important`,
    fontSize: "13px",
    fontWeight: 600,
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: ({ palette: { text } }) => `${text.primary} !important`,
  },
  "& .MuiListItemText-primary": {
    color: ({ palette: { text } }) => text.primary,
    fontSize: "14px",
  },
};

const PLACEHOLDER = (text) => (
  <span style={{ color: TC.primary, fontSize: "13px", opacity: 0.9, fontWeight: 600 }}>{text}</span>
);

function toRoleId(value) {
  return value
    .toString()
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_")
    .replace(/[^a-z0-9_]/g, "");
}

function normalizeMultiSelectValue(value) {
  if (Array.isArray(value)) return value;
  if (typeof value === "string") {
    return value
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
  }
  return [];
}

function TenantRoleAssignmentManager() {
  const { isSuperAdmin, isTenantAdmin, role: currentRole, tenantId, permissions } = useAuthz();
  const { selectedCompanyId } = useCompany();
  const activeTenantId = useMemo(
    () => (isSuperAdmin ? selectedCompanyId || "" : tenantId || ""),
    [isSuperAdmin, selectedCompanyId, tenantId]
  );

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [availablePermissions, setAvailablePermissions] = useState([]);
  const [roleDraft, setRoleDraft] = useState({
    roleId: "",
    name: "",
    permissions: [],
  });
  const [assignmentDraft, setAssignmentDraft] = useState({
    uid: "",
    roleIds: [],
  });

  const loadData = useCallback(async () => {
    if (!activeTenantId) {
      setUsers([]);
      setRoles([]);
      setAvailablePermissions([]);
      setError("Select a tenant scope to load users, roles, and permissions.");
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      const [tenantUsersResult, tenantRolesResult] = await Promise.allSettled([
        listTenantUsers({ tenantId: activeTenantId, limit: 200 }),
        listTenantRoles({ tenantId: activeTenantId }),
      ]);

      const loadErrors = [];

      if (tenantUsersResult.status === "fulfilled") {
        setUsers(Array.isArray(tenantUsersResult.value?.users) ? tenantUsersResult.value.users : []);
      } else {
        setUsers([]);
        loadErrors.push(tenantUsersResult.reason?.message || "Failed to load tenant users.");
      }

      if (tenantRolesResult.status === "fulfilled") {
        setRoles(Array.isArray(tenantRolesResult.value?.roles) ? tenantRolesResult.value.roles : []);
        setAvailablePermissions(
          Array.isArray(tenantRolesResult.value?.availablePermissions)
            ? tenantRolesResult.value.availablePermissions
            : []
        );
      } else {
        setRoles([]);
        setAvailablePermissions([]);
        loadErrors.push(tenantRolesResult.reason?.message || "Failed to load tenant roles.");
      }

      if (loadErrors.length > 0) {
        setError(loadErrors.join(" "));
      }
    } finally {
      setIsLoading(false);
    }
  }, [activeTenantId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleCreateRole = async () => {
    setError("");
    setSuccess("");

    if (!activeTenantId) {
      setError("Select a tenant before creating roles.");
      return;
    }

    const roleId = roleDraft.roleId ? toRoleId(roleDraft.roleId) : toRoleId(roleDraft.name);
    if (!roleId || !roleDraft.name.trim()) {
      setError("Role ID and role name are required.");
      return;
    }

    try {
      await createTenantRole({
        tenantId: activeTenantId,
        roleId,
        name: roleDraft.name.trim(),
        permissions: roleDraft.permissions,
      });

      setRoleDraft({ roleId: "", name: "", permissions: [] });
      setSuccess("Role saved successfully.");
      await loadData();
    } catch (saveError) {
      setError(saveError?.message || "Failed to save tenant role.");
    }
  };

  const handleAssignRoles = async () => {
    setError("");
    setSuccess("");

    if (!activeTenantId) {
      setError("Select a tenant before assigning roles.");
      return;
    }

    if (!assignmentDraft.uid) {
      setError("Select a user to assign roles.");
      return;
    }

    try {
      await assignTenantUserRoles({
        tenantId: activeTenantId,
        uid: assignmentDraft.uid,
        roleIds: assignmentDraft.roleIds,
      });

      setSuccess("Roles assigned successfully.");
      await loadData();
    } catch (assignError) {
      setError(assignError?.message || "Failed to assign roles.");
    }
  };

  const editableUsers = users.filter((user) => {
    if (user.role === "super_admin") return false;
    if (currentRole === "tenant_admin") return user.role === "user";
    return true;
  });
  const selectedUser = editableUsers.find((user) => (user.uid || user.id) === assignmentDraft.uid) || null;
  const roleNameById = useMemo(
    () => new Map(roles.map((role) => [role.roleId || role.id, role.name || role.roleId || role.id])),
    [roles]
  );

  if (!isSuperAdmin && !isTenantAdmin) {
    return (
      <Card>
        <VuiBox p={3}>
          <VuiTypography color="warning" variant="button">
            Only super admins or tenant admins can manage tenant role assignments.
          </VuiTypography>
        </VuiBox>
      </Card>
    );
  }

  return (
    <VuiBox>
      <Grid container spacing={3}>
        <Grid item xs={12} lg={6}>
          <Card>
            <VuiBox p={3}>
              <VuiTypography variant="lg" mb={2} sx={{ color: TC.primary }}>
                Create or Update Tenant Role
              </VuiTypography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <VuiInput
                    placeholder="Role Name (e.g. Payroll Manager)"
                    value={roleDraft.name}
                    onChange={(event) =>
                      setRoleDraft((prev) => ({ ...prev, name: event.target.value }))
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <VuiInput
                    placeholder="Role ID (optional - auto-generated from name)"
                    value={roleDraft.roleId}
                    onChange={(event) =>
                      setRoleDraft((prev) => ({ ...prev, roleId: event.target.value }))
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth sx={selectFieldSx}>
                    <Select
                                             multiple
                       displayEmpty
                       value={roleDraft.permissions}
                      onChange={(event) =>
                        setRoleDraft((prev) => ({
                          ...prev,
                          permissions: normalizeMultiSelectValue(event.target.value),
                        }))
                      }
                      input={<OutlinedInput />}
                      renderValue={(selected) => selected.length > 0 ? selected.map((p) => PERMISSION_LABELS[p] || p).join(", ") : PLACEHOLDER("Permissions")}
                      MenuProps={SELECT_MENU_PROPS}
                    >
                      {availablePermissions.length > 0 ? (
                        availablePermissions.map((permission) => (
                          <MenuItem key={permission} value={permission}>
                            <Checkbox checked={roleDraft.permissions.includes(permission)} />
                            <ListItemText primary={PERMISSION_LABELS[permission] || permission} />
                          </MenuItem>
                        ))
                      ) : (
                        <MenuItem value="" disabled>
                          No permissions available.
                        </MenuItem>
                      )}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              <VuiBox mt={3} display="flex" justifyContent="flex-end">
                <VuiButton color="info" onClick={handleCreateRole}>
                  Save Role
                </VuiButton>
              </VuiBox>
            </VuiBox>
          </Card>
        </Grid>

        <Grid item xs={12} lg={6}>
          <Card>
            <VuiBox p={3}>
              <VuiTypography variant="lg" mb={2} sx={{ color: TC.primary }}>
                Assign Roles to User
              </VuiTypography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormControl fullWidth sx={selectFieldSx}>
                    <Select
                       displayEmpty
                       value={assignmentDraft.uid}

                      renderValue={(selected) => { if (!selected) { return PLACEHOLDER("Select a user"); } const user = editableUsers.find((item) => (item.uid || item.id) === selected);
                        return user?.displayName || user?.email || selected;
                      }}
                      onChange={(event) => {
                        const uid = event.target.value;
                        const user = editableUsers.find((item) => (item.uid || item.id) === uid);
                        setAssignmentDraft({ uid, roleIds: Array.isArray(user?.assignedRoleIds) ? user.assignedRoleIds : [] });
                      }}
                      MenuProps={SELECT_MENU_PROPS}
                    >
                      {editableUsers.length > 0 ? (
                        editableUsers.map((user) => (
                          <MenuItem key={user.uid || user.id} value={user.uid || user.id}>
                            {user.displayName || user.email}
                          </MenuItem>
                        ))
                      ) : (
                        <MenuItem value="" disabled>
                          No eligible users available for role assignment.
                        </MenuItem>
                      )}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth sx={selectFieldSx}>
                    <Select
                                             multiple
                       displayEmpty
                                            value={assignmentDraft.roleIds}
                      onChange={(event) =>
                        setAssignmentDraft((prev) => ({
                          ...prev,
                          roleIds: normalizeMultiSelectValue(event.target.value),
                        }))
                      }
                      input={<OutlinedInput />}
                      renderValue={(selected) => selected.length > 0 ? selected.map((roleId) => roleNameById.get(roleId) || roleId).join(", ") : PLACEHOLDER("Roles")}
                      MenuProps={SELECT_MENU_PROPS}
                    >
                      {roles.length > 0 ? (
                        roles.map((role) => (
                          <MenuItem key={role.roleId || role.id} value={role.roleId || role.id}>
                            <Checkbox checked={assignmentDraft.roleIds.includes(role.roleId || role.id)} />
                            <ListItemText primary={role.name || role.roleId || role.id} />
                          </MenuItem>
                        ))
                      ) : (
                        <MenuItem value="" disabled>
                          No roles available.
                        </MenuItem>
                      )}
                    </Select>
                  </FormControl>
                </Grid>
                {selectedUser ? (
                  <Grid item xs={12}>
                    <VuiTypography variant="caption" sx={{ color: TC.secondary }}>
                      Base role: {selectedUser.role || "user"}
                    </VuiTypography>
                  </Grid>
                ) : null}
              </Grid>

              <VuiBox mt={3} display="flex" justifyContent="flex-end">
                <VuiButton color="info" onClick={handleAssignRoles}>
                  Assign Roles
                </VuiButton>
              </VuiBox>
            </VuiBox>
          </Card>
        </Grid>
      </Grid>

      <VuiBox mt={2}>
        {isLoading ? (
          <LoadingSpinner size="sm" label="Loading role data…" centered={false} />
        ) : null}
        {error ? (
          <VuiTypography color="error" variant="button" display="block">
            {error}
          </VuiTypography>
        ) : null}
        {success ? (
          <VuiTypography color="success" variant="button" display="block">
            {success}
          </VuiTypography>
        ) : null}
      </VuiBox>
    </VuiBox>
  );
}

export default TenantRoleAssignmentManager;


